import React, { FC, useState, useEffect, useCallback } from "react"

import { ReactComponent as CheckIcon } from "@common_assets/svg/check.svg"
import { ReactComponent as CrossIcon } from "@common_assets/svg/cross.svg"
import { ReactComponent as EyeClosedIcon } from "@common_assets/svg/eye-closed.svg"
import { ReactComponent as EyeOpenIcon } from "@common_assets/svg/eye-open.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Grid,
  InputAdornment,
  Typography,
  useTheme,
  Stack,
} from "@mui/material"
import { useMutationChangePassword } from "api/reactQuery/mutations/teacher"
import TextField from "components/form/common/field/textField"
import { useCustomSnackbar } from "hooks/snackbar"
import { useForm, FormProvider, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { passwordSchema } from "utils/yup"

import { defaultValues, validateSchema } from "./Password.config"
import { IFormState } from "./Password.types"

interface IProps {
  id?: string
  handleClose?: () => void
}

const Password: FC<IProps> = ({ id, handleClose }) => {
  const [showNewPass, setShowNewPass] = useState(false)
  const [showCurrentPass, setShowCurrentPass] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const theme = useTheme()
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()

  const methods = useForm<IFormState>({
    resolver: yupResolver(validateSchema),
    defaultValues: defaultValues,
  })

  const password = useWatch({ name: "password", control: methods.control })

  useEffect(() => {
    if (password && password.length > 0) {
      passwordSchema
        .isValid(password)
        .then((isValid: boolean) => {
          setIsPasswordValid(isValid)
        })
        .catch(() => {
          setIsPasswordValid(false)
        })
    } else {
      setIsPasswordValid(false)
    }
  }, [password])

  const { mutate } = useMutationChangePassword({
    options: {
      onSuccess: () => {
        showSnackbar({
          title: t("teacherSettings.passwordUpdatedSuccessfully"),
        })
        handleClose && handleClose()
      },
      onError: (err) => {
        if (err.response?.status === 400) {
          showSnackbar({
            title: t("teacherSettings.incorrectCurrentPassword"),
            variant: "error",
          })
        }
      },
    },
  })

  const onSubmit = (data: IFormState) => mutate(data)

  const showPasswordSign = useCallback(() => {
    if (!password || password.length === 0) {
      return null
    }

    if (isPasswordValid) {
      return (
        <CheckIcon
          style={{
            color: theme.palette.mockup.success50,
          }}
        />
      )
    }

    if (!isPasswordValid) {
      return (
        <CrossIcon
          style={{
            color: theme.palette.mockup.error50,
          }}
        />
      )
    }

    return null
  }, [
    isPasswordValid,
    password,
    theme.palette.mockup.error50,
    theme.palette.mockup.success50,
  ])

  return (
    <Stack>
      <Typography
        sx={{ mt: 4, mb: 3 }}
        variant="subtitle1"
        color={theme.palette.mockup.neutral10}
      >
        {t("teacherSettings.accountDetails")}
      </Typography>
      <form id={id} onSubmit={methods.handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Grid container>
            <Grid size={{ desktop: 6, mobile: 12 }}>
              <TextField
                name="current_password"
                type={showCurrentPass ? "text" : "password"}
                fullWidth
                label={t("teacherSettings.currentPassword")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      component="button"
                      type="button"
                      aria-label={
                        showCurrentPass
                          ? t("teacherSettings.hideCurrentPassword")
                          : t("teacherSettings.showCurrentPassword")
                      }
                      sx={[
                        {
                          cursor: "pointer",
                          color: "primary.main",
                          border: "none",
                          background: "none",
                          padding: 0,
                          margin: 0,
                          fontFamily: "inherit",
                        },
                        (theme) => ({
                          "&:hover": {
                            color: theme.palette.mockup.primary80,
                          },
                        }),
                      ]}
                      position={"start"}
                      onClick={() => setShowCurrentPass(!showCurrentPass)}
                    >
                      {showCurrentPass ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Grid container sx={{ my: 4 }}>
            <Grid size={{ desktop: 6, mobile: 12 }}>
              <TextField
                name="password"
                type={showNewPass ? "text" : "password"}
                fullWidth
                label={t("teacherSettings.newPassword")}
                slotProps={{
                  formHelperText: {
                    sx: {
                      position: "relative",
                      transform: "translateY(0px)",
                    },
                  },
                  input: {
                    endAdornment: (
                      <>
                        {showPasswordSign()}
                        <InputAdornment
                          component="button"
                          type="button"
                          aria-label={
                            showNewPass
                              ? t("teacherSettings.hideNewPassword")
                              : t("teacherSettings.showNewPassword")
                          }
                          sx={[
                            {
                              cursor: "pointer",
                              color: "primary.main",
                              border: "none",
                              background: "none",
                              padding: 0,
                              margin: 0,
                              fontFamily: "inherit",
                            },
                            (theme) => ({
                              "&:hover": {
                                color: theme.palette.mockup.primary80,
                              },
                            }),
                          ]}
                          position={"start"}
                          onClick={() => setShowNewPass(!showNewPass)}
                        >
                          {showNewPass ? <EyeOpenIcon /> : <EyeClosedIcon />}
                        </InputAdornment>
                      </>
                    ),
                  },
                }}
              />
            </Grid>
          </Grid>
        </FormProvider>
      </form>
      <Typography
        variant="body2"
        sx={{ fontWeight: "500", mt: 2 }}
        color={theme.palette.mockup.neutral0}
      >
        {t("teacherSettings.forgotYourPassword")}
        <Link
          onClick={handleClose}
          to={RoutePath.RESET_PASSWORD}
          style={{
            color: theme.palette.mockup.primary20,
            paddingLeft: "5px",
          }}
        >
          {t("teacherSettings.reset")}
        </Link>
      </Typography>
    </Stack>
  )
}

export default Password
